import { Box, Button, Flex, Heading, Progress } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";

import { dateFormatter } from "../../../shared/utils/date-formatter";
import { downloadCsvFileData, jsonToCSV } from "../../../shared/utils/csv-utils";
import TasksNewFilters, { usePatientTaskTemplatesQuery } from "./components/TasksNewFilters";
import { useTasksNewAiCapabilities } from "./tasksNew.ai";
import TasksNewPagination from "./components/TasksNewPagination";
import TasksNewTable from "./components/TasksNewTable";
import { MergedRow, useTasksNewData } from "./hooks/useTasksNewData";
import { TasksNewFiltersProvider, useTasksNewFilters } from "./hooks/useTasksNewFilters";

function formatFullName(
  p: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!p) return "";
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ");
}

function statusLabel(row: MergedRow): string {
  if (row.__kind !== "regular") return "Incompleted";
  switch (row.status) {
    case "COMPLETED":
      return "Completed";
    case "APPROVED":
      return "Approved";
    case "INCOMPLETED":
    default:
      return "Incompleted";
  }
}

function rowsToCsv(rows: MergedRow[]): string {
  const csvRows = rows.map((row) => ({
    Patient: formatFullName(row.patient),
    Caregiver: formatFullName(row.caregiver),
    "Task name": row.taskName ?? "",
    Contract: row.contract?.contractType?.name ?? "",
    "Created at": row.createdAt ? dateFormatter.toDateOrDateTime(row.createdAt) : "",
    "Created by":
      row.__kind === "regular" && row.createdByMember ? formatFullName(row.createdByMember) : "",
    "Scheduled date": row.scheduledDate ? dateFormatter.toDate(row.scheduledDate) : "",
    Status: statusLabel(row),
    "Clock in":
      row.__kind === "regular" && row.clockInTime ? dateFormatter.toTime(row.clockInTime) : "",
    "Clock out":
      row.__kind === "regular" && row.clockOutTime ? dateFormatter.toTime(row.clockOutTime) : "",
  }));
  return jsonToCSV(csvRows);
}

function TasksNewPageContent() {
  const { rows, isPending, isFetching } = useTasksNewData();
  const filters = useTasksNewFilters();
  const templates = usePatientTaskTemplatesQuery();

  // This tab tells the omnibar what its own filters are, while it is on screen.
  useTasksNewAiCapabilities({ filters, templates: templates.data });

  const showProgressBar = isFetching && !isPending;

  function handleExport() {
    const csv = rowsToCsv(rows);
    const today = dateFormatter.toDate(LocalDate.now());
    downloadCsvFileData(`tasks-${today}.csv`, csv);
  }

  return (
    <Box p={4}>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">Tasks (new)</Heading>
        <Button isDisabled={rows.length === 0} size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </Flex>
      <TasksNewFilters />
      <Box mt={4} position="relative">
        {showProgressBar && (
          <Progress
            isIndeterminate
            left={0}
            position="absolute"
            right={0}
            size="xs"
            top={0}
            zIndex={1}
          />
        )}
        <TasksNewTable />
      </Box>
      <TasksNewPagination />
    </Box>
  );
}

export default function TasksNewPage() {
  return (
    <TasksNewFiltersProvider>
      <TasksNewPageContent />
    </TasksNewFiltersProvider>
  );
}
